import { StepShell, Repeatable } from '../components/layout';
import { TextField, SelectField, Checkbox } from '../components/inputs';

const blankEntry = () => ({
  _id: `edu_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  level: '',
  institution: '',
  board_university: '',
  field_of_study: '',
  passing_year: '',
  is_in_progress: false,
  grade_type: 'not_specified',
  grade_value: '',
});

export default function EducationStep({ data, update, notes = {}, config }) {
  const entries = data.entries?.length ? data.entries : [blankEntry()];

  const setEntries = (next) => update({ entries: next });

  const patch = (index, changes) => {
    setEntries(entries.map((entry, i) => (i === index ? { ...entry, ...changes } : entry)));
  };

  const decorated = entries.map((entry, index) => ({
    ...entry,
    _onRemove: () => setEntries(entries.filter((_, i) => i !== index)),
  }));

  return (
    <StepShell
      eyebrow="Step 3 of 18"
      title="Your learning so far"
      lede="Add whatever you have studied. If you are still in the middle of something, that counts too — just tick the box."
      why="Knowing where you are in your studies helps us match you with a workload and a schedule that actually fits around it."
    >
      <Repeatable
        entries={decorated}
        minEntries={1}
        addLabel="Add another qualification"
        titleFor={(entry, index) => entry.level || `Qualification ${index + 1}`}
        onAdd={() => setEntries([...entries, blankEntry()])}
        renderEntry={(entry, index) => (
          <>
            <SelectField
              label="Level"
              options={config.qualificationLevels}
              placeholder="What level was this?"
              value={entry.level}
              note={notes[`entries.${index}.level`]}
              onChange={(v) => patch(index, { level: v })}
            />

            <TextField
              label="Institution"
              placeholder="School, college or university"
              value={entry.institution}
              note={notes[`entries.${index}.institution`]}
              onChange={(v) => patch(index, { institution: v })}
            />

            <div className="ob-grid2">
              <TextField
                label="Board or university"
                optional
                placeholder="e.g. HEC, Federal Board, BISE Lahore, NED University"
                value={entry.board_university}
                onChange={(v) => patch(index, { board_university: v })}
              />

              <TextField
                label="Field of study"
                optional
                placeholder="e.g. Computer Science"
                value={entry.field_of_study}
                onChange={(v) => patch(index, { field_of_study: v })}
              />
            </div>

            <Checkbox
              label="I am still studying this"
              checked={entry.is_in_progress}
              onChange={(checked) => patch(index, {
                is_in_progress: checked,
                grade_type: checked ? 'in_progress' : 'not_specified',
              })}
            />

            {!entry.is_in_progress && (
              <div className="ob-grid2">
                <TextField
                  label="Year completed"
                  optional
                  inputMode="numeric"
                  placeholder="2025"
                  value={entry.passing_year}
                  note={notes[`entries.${index}.passing_year`]}
                  onChange={(v) => patch(index, { passing_year: v })}
                />

                <SelectField
                  label="Result"
                  optional
                  options={config.gradeTypes}
                  placeholder="How was it graded?"
                  value={entry.grade_type}
                  note={notes[`entries.${index}.grade_type`]}
                  onChange={(v) => patch(index, { grade_type: v })}
                />
              </div>
            )}

            {!entry.is_in_progress &&
             entry.grade_type &&
             !['not_specified', 'in_progress'].includes(entry.grade_type) && (
              <TextField
                label={
                  entry.grade_type === 'gpa' ? 'GPA'
                    : entry.grade_type === 'percentage' ? 'Percentage'
                    : 'Marks obtained'
                }
                placeholder={
                  entry.grade_type === 'gpa' ? '3.6'
                    : entry.grade_type === 'percentage' ? '82'
                    : '850 / 1100'
                }
                value={entry.grade_value}
                onChange={(v) => patch(index, { grade_value: v })}
              />
            )}
          </>
        )}
      />
    </StepShell>
  );
}
